import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Skeleton,
  useTheme,
  alpha,
  Fade,
  Zoom
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { InventoryItem } from '../../utils/types';
import { useCart } from '../../contexts/CartContext';
import { useCurrency } from '../../contexts/CurrencyContext';
// استخدام مسار نسبي للصورة الافتراضية
const placeholderImage = '/assets/images/product-placeholder.png';

interface MobileOptimizedProductCardProps {
  product: InventoryItem;
  layout?: 'grid' | 'list';
  showQuickActions?: boolean;
  priority?: boolean;
}

const MobileOptimizedProductCard: React.FC<MobileOptimizedProductCardProps> = memo(({
  product,
  layout = 'grid',
  showQuickActions = true,
  priority = false
}) => {
  const theme = useTheme();
  const { addToCart, isInCart } = useCart();
  const { formatCurrency } = useCurrency();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
  };

  const handleFavoriteToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorite(!isFavorite);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: product.description || 'منتج رائع من متجرنا',
        url: window.location.origin + `/mobile-store/product/${product.id}`
      });
    }
  };

  const isOutOfStock = product.quantity <= 0;
  const isLowStock = product.quantity <= 5 && product.quantity > 0;

  if (layout === 'list') {
    return (
      <Card
        className="product-card"
        sx={{
          display: 'flex',
          mb: 2,
          borderRadius: 3,
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
            borderColor: alpha(theme.palette.primary.main, 0.3)
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CardActionArea
          component={Link}
          to={`/mobile-store/product/${product.id}`}
          sx={{ display: 'flex', alignItems: 'stretch' }}
        >
          <Box sx={{ position: 'relative', width: 120, flexShrink: 0 }}>
            {!imageLoaded && (
              <Skeleton
                variant="rectangular"
                width={120}
                height={120}
                sx={{ borderRadius: 1 }}
              />
            )}
            <CardMedia
              component="img"
              sx={{
                width: 120,
                height: 120,
                objectFit: 'contain',
                p: 1,
                display: imageLoaded ? 'block' : 'none'
              }}
              image={product.imageUrl || placeholderImage}
              alt={product.name}
              loading={priority ? 'eager' : 'lazy'}
              onLoad={() => setImageLoaded(true)}
            />
            {isOutOfStock && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: alpha(theme.palette.common.black, 0.6),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1
                }}
              >
                <Typography variant="caption" color="white" fontWeight="bold">
                  نفذت الكمية
                </Typography>
              </Box>
            )}
          </Box>

          <CardContent sx={{ flex: 1, p: 2 }}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {product.name}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {product.description || 'لا يوجد وصف متاح'}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" color="primary" fontWeight="bold">
                {formatCurrency(product.sellingPrice)}
              </Typography>

              {isLowStock && (
                <Chip
                  label={`متبقي ${product.quantity}`}
                  color="warning"
                  size="small"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </CardContent>
        </CardActionArea>

        <Box sx={{ display: 'flex', flexDirection: 'column', p: 1, gap: 1 }}>
          <IconButton
            size="small"
            onClick={handleFavoriteToggle}
            sx={{
              color: isFavorite ? theme.palette.error.main : theme.palette.grey[400],
              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) }
            }}
          >
            {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          </IconButton>

          <IconButton
            size="small"
            onClick={handleAddToCart}
            disabled={isOutOfStock || isInCart(product.id)}
            sx={{
              color: theme.palette.primary.main,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
            }}
          >
            <ShoppingCartIcon />
          </IconButton>
        </Box>
      </Card>
    );
  }

  return (
    <Fade in timeout={300}>
      <Card
        className="product-card"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
            borderColor: alpha(theme.palette.primary.main, 0.3)
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Quick Actions */}
        {showQuickActions && (
          <Zoom in={isHovered} timeout={200}>
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}
            >
              <IconButton
                size="small"
                onClick={handleFavoriteToggle}
                sx={{
                  bgcolor: alpha(theme.palette.background.paper, 0.9),
                  color: isFavorite ? theme.palette.error.main : theme.palette.grey[600],
                  '&:hover': {
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.main
                  }
                }}
              >
                {isFavorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
              </IconButton>

              <IconButton
                size="small"
                onClick={handleShare}
                sx={{
                  bgcolor: alpha(theme.palette.background.paper, 0.9),
                  color: theme.palette.grey[600],
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }
                }}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            </Box>
          </Zoom>
        )}

        {/* Stock Status Badge */}
        {(isOutOfStock || isLowStock) && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 2
            }}
          >
            <Chip
              label={isOutOfStock ? 'نفذت الكمية' : `متبقي ${product.quantity}`}
              color={isOutOfStock ? 'error' : 'warning'}
              size="small"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 'bold',
                boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`
              }}
            />
          </Box>
        )}

        <CardActionArea
          component={Link}
          to={`/mobile-store/product/${product.id}`}
          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
        >
          <Box sx={{ position: 'relative', height: { xs: 130, sm: 160, md: 200 } }}>
            {!imageLoaded && (
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                sx={{ position: 'absolute', top: 0, left: 0 }}
              />
            )}
            <CardMedia
              component="img"
              height="100%"
              image={product.imageUrl || placeholderImage}
              alt={product.name}
              loading={priority ? 'eager' : 'lazy'}
              onLoad={() => setImageLoaded(true)}
              sx={{
                objectFit: 'contain',
                p: { xs: 1, sm: 1.5, md: 2 },
                display: imageLoaded ? 'block' : 'none',
                filter: isOutOfStock ? 'grayscale(100%)' : 'none',
                opacity: isOutOfStock ? 0.6 : 1
              }}
            />
          </Box>

          <CardContent sx={{ flexGrow: 1, p: { xs: 1, sm: 1.5, md: 2 } }}>
            <Typography
              gutterBottom
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '0.78rem', sm: '0.9rem', md: '1rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                minHeight: '2.4em',
                mb: 0.5
              }}
            >
              {product.name}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: { xs: 1, sm: 1.5, md: 2 },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                minHeight: '2.4em',
                fontSize: { xs: '0.68rem', sm: '0.75rem', md: '0.875rem' }
              }}
            >
              {product.description || 'لا يوجد وصف متاح'}
            </Typography>

            <Typography
              variant="h6"
              color="primary"
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' } }}
            >
              {formatCurrency(product.sellingPrice)}
            </Typography>
          </CardContent>
        </CardActionArea>

        <CardActions sx={{ p: { xs: 1, sm: 1.5, md: 2 }, pt: 0 }}>
          <Button
            size="small"
            color="primary"
            variant="contained"
            fullWidth
            startIcon={<ShoppingCartIcon sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }} />}
            onClick={handleAddToCart}
            disabled={isOutOfStock || isInCart(product.id)}
            sx={{
              borderRadius: 2,
              py: { xs: 0.5, sm: 0.75, md: 1 },
              fontWeight: 'bold',
              fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.875rem' },
              '&:disabled': {
                bgcolor: alpha(theme.palette.grey[400], 0.3)
              }
            }}
          >
            {isOutOfStock ? 'نفذت الكمية' : isInCart(product.id) ? 'في السلة' : 'أضف للسلة'}
          </Button>
        </CardActions>
      </Card>
    </Fade>
  );
});

MobileOptimizedProductCard.displayName = 'MobileOptimizedProductCard';

export default MobileOptimizedProductCard;
